import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";
import { useAppConfig } from "../ConfigContext";
import {
  getNameStyleCSS,
  getUserBannerStyle,
  getUserNameCosmetics,
} from "./GameNameStyles";
import "../index.css";

const API_BASE = process.env.REACT_APP_API_URL;

function formatNum(value) {
  const n = Number(value || 0);

  if (!Number.isFinite(n)) {
    return "0";
  }

  return n.toLocaleString();
}

function svgFallback(name = "?") {
  const initial = (name?.[0] || "?").toUpperCase();

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
      <rect width="100%" height="100%" fill="#914422"/>
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        font-size="32"
        fill="#fff"
        font-family="Arial, sans-serif"
      >${initial}</text>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

function PublicStatBox({
  icon,
  fallbackIcon,
  label,
  value,
}) {
  return (
    <div
      style={{
        minWidth: 0,
        background: "rgba(80, 35, 16, 0.7)",
        border: "2px solid #5d2a13",
        borderRadius: 6,
        padding: 6,
        textAlign: "center",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 15,
          lineHeight: "18px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </div>

      <img
        src={icon}
        alt=""
        style={{
          height: 32,
          objectFit: "contain",
          marginTop: 5,
          pointerEvents: "none",
        }}
        onError={(event) => {
          if (fallbackIcon) {
            event.currentTarget.src = fallbackIcon;
          } else {
            event.currentTarget.style.display = "none";
          }
        }}
      />

      <div
        className="shadow1"
        style={{
          color: "#f3e14d",
          fontSize: 16,
          lineHeight: "20px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {formatNum(value)}
      </div>
    </div>
  );
}

export default function PublicProfileModal({
  userId,
  onClose,
  onNavigate,
}) {
  const { season } = useAppConfig();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [privateProfile, setPrivateProfile] = useState(false);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    async function loadPublicProfile() {
      try {
        setLoading(true);
        setError("");
        setPrivateProfile(false);
        setProfile(null);

        const token = localStorage.getItem("token");

        const res = await fetch(
          `${API_BASE}/api/users/${encodeURIComponent(
            userId
          )}/public-profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-TG-INITDATA":
                window.Telegram?.WebApp?.initData || "",
            },
          }
        );

        const data = await res.json().catch(() => ({}));

        if (cancelled) return;

        if (res.status === 403 && data?.private === true) {
          setPrivateProfile(true);
          return;
        }

        if (!res.ok) {
          throw new Error(
            data?.error || "Could not load public profile."
          );
        }

        setProfile(data);
      } catch (loadError) {
        console.error(
          "[PUBLIC PROFILE LOAD ERROR]",
          loadError
        );

        if (!cancelled) {
          setError(
            loadError?.message ||
              "Could not load public profile."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPublicProfile();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userId) {
    return null;
  }

  const name =
    profile?.name ||
    profile?.username ||
    "Player";
const avatar =
  profile?.avatarUrl ||
  profile?.avatar_url ||
  profile?.photo_url ||
  profile?.avatar ||
  svgFallback(name);

const currentSeasonNumber =
  Number(
    season?.season_number ??
    season?.seasonNumber ??
    season?.number ??
    season?.id ??
    season
  );

const gamePassSeasonNumber =
  Number(
    profile?.gamepass_season_id ??
    profile?.gamepassSeasonId ??
    profile?.game_pass_season_id
  );

const gamePassActive =
  Boolean(
    profile?.gamepass_active ??
    profile?.gamepassActive ??
    profile?.game_pass_active
  ) &&
  Number.isFinite(currentSeasonNumber) &&
  Number.isFinite(gamePassSeasonNumber) &&
  gamePassSeasonNumber ===
    currentSeasonNumber;

const battlePassActive = Boolean(
  profile?.battle_pass_active ??
    profile?.battlePassActive ??
    profile?.battlepass_active
);

const openPassPage = (page) => {
  onClose?.();
  onNavigate?.(page);
};

const level = Math.max(
  0,
  Math.min(Number(profile?.level) || 0, 30)
);

  const levelPercent = Math.max(
    0,
    Math.min(100, (level / 30) * 100)
  );

  const side = String(
    profile?.side || "good"
  ).toLowerCase();

  const isMoon =
    side === "bad" ||
    side === "moon";

  const sideLabel = isMoon
  ? "Moon Anubis"
  : "Sun Pharaohs";

  const sideIcon =
    isMoon ? "/v2/bad.png" : "/v2/good.png";

  const {
    bannerSrc,
    emojiSrc,
  } = profile
    ? getUserNameCosmetics(profile)
    : {
        bannerSrc: null,
        emojiSrc: null,
      };

  const profileBannerStyle =
    profile && bannerSrc
      ? getUserBannerStyle(profile)
      : {
          backgroundColor: "#6a4628",
          backgroundImage: "none",
        };

  const nameStyle =
    profile
      ? getNameStyleCSS(
          profile?.active_name_style ??
            profile?.activeNameStyle
        )
      : {};

  return (
    <div className="modal_box">
      <div className="modal_title">
        Player Profile

        <button
          type="button"
          className="modal_close"
          onClick={onClose}
        />
      </div>

      <motion.div
        className="modal_body"
        initial={{
          scale: 0.8,
          opacity: 0,
        }}
        animate={{
          scale: 1,
          opacity: 1,
        }}
        exit={{
          scale: 0.92,
          opacity: 0,
        }}
      >
        <div className="modal_inside_2">
          {loading ? (
            <div
              style={{
                minHeight: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoadingSpinner
                text="Loading Profile..."
                size={56}
              />
            </div>
          ) : privateProfile ? (
            <div
              className="info shadow1"
              style={{
                color: "#fff",
                fontSize: 17,
                lineHeight: "22px",
                fontWeight: 600,
                textAlign: "center",
                padding: 20,
              }}
            >
              This Player's Profile is Private.
            </div>
          ) : error ? (
            <div
              className="info shadow1"
              style={{
                color: "#ff8178",
                fontSize: 16,
                lineHeight: "20px",
                fontWeight: 600,
                textAlign: "center",
                padding: 20,
              }}
            >
              {error}
            </div>
          ) : profile ? (
            <>
              {/* USER HEADER */}
<div
  style={{
    ...profileBannerStyle,

    width: "100%",
    minHeight: 64,
padding: 7,
    boxSizing: "border-box",
    borderRadius: 8,

    border: "2px solid #401f0e",

    display: "flex",
    alignItems: "center",
    gap: 10,

    overflow: "hidden",

    boxShadow: "none",
    outline: "none",
  }}
>
  {/* AVATAR */}
  <img
    src={avatar}
    alt=""
    onError={(event) => {
      event.currentTarget.onerror = null;
      event.currentTarget.src =
        svgFallback(name);
    }}
    style={{
      width: 48,
height: 48,
flex: "0 0 48px",
      borderRadius: 8,
      objectFit: "cover",
      border: "2px solid #5d2a13",
      boxSizing: "border-box",
    }}
  />

  {/* NAME / SIDE */}
  <div
    style={{
      flex: 1,
      minWidth: 0,
      textAlign: "left",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {emojiSrc && (
        <img
          src={emojiSrc}
          alt=""
          draggable={false}
          style={{
            width: 24,
            height: 24,
            flex: "0 0 24px",
            objectFit: "contain",
            borderRadius: "6px",
          }}
        />
      )}

      <span
        title={name}
        style={{
          display: "inline-block",
          minWidth: 0,
          maxWidth: "100%",

          color: "#fff",
          fontSize: 21,
          lineHeight: "24px",
          fontWeight: 700,

          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",

          ...getNameStyleCSS(
            profile?.active_name_style ??
              profile?.activeNameStyle ??
              ""
          ),
        }}
      >
        {name}
      </span>
    </div>
  </div>
</div>


{/* USER PASSES */}
<div
  style={{
    width: "100%",
    marginTop: 8,
    display: "grid",
    gridTemplateColumns:
      "repeat(2, minmax(0, 1fr))",
    gap: 8,
    boxSizing: "border-box",
  }}
>
  {/* GAME PASS */}
  <button
    type="button"
    onClick={() => openPassPage("gamepass")}
    style={{
      width: "100%",
      minWidth: 0,
      minHeight: 52,
      padding: "6px 8px",

      borderRadius: 8,

      border: gamePassActive
        ? "2px solid #77c943"
        : "2px solid #74332a",

      background: gamePassActive
        ? "linear-gradient(180deg, #568d2f 0%, #31581c 100%)"
        : "linear-gradient(180deg, #75402e 0%, #4b271d 100%)",

      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.18), 0 3px 0 rgba(42,16,7,0.7)",

      display: "flex",
      alignItems: "center",
      gap: 7,

      cursor: "pointer",
      boxSizing: "border-box",
    }}
  >
    <img
      src="/pass1.png"
      alt=""
      style={{
        width: 34,
        height: 34,
        flexShrink: 0,
        objectFit: "contain",
      }}
    />

    <div
      style={{
        minWidth: 0,
        flex: 1,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 12,
          lineHeight: "16px",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        OG PASS
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 2,
          color: gamePassActive
            ? "#c8ff91"
            : "#ff9b8e",
          fontSize: 11,
          lineHeight: "13px",
          fontWeight: 700,
        }}
      >
        {gamePassActive
          ? "ACTIVE"
          : "INACTIVE"}
      </div>
    </div>

    <img
      src="/v2/drop.png"
      alt=""
      draggable={false}
      style={{
        width: 12,
        height: 8,
        flexShrink: 0,
        objectFit: "contain",
        transform: "rotate(-90deg)",
      }}
    />
  </button>

  {/* BATTLE PASS */}
  <button
    type="button"
    onClick={() => openPassPage("battlePass")}
    style={{
      width: "100%",
      minWidth: 0,
      minHeight: 52,
      padding: "6px 8px",

      borderRadius: 8,

      border: battlePassActive
        ? "2px solid #77c943"
        : "2px solid #74332a",

      background: battlePassActive
        ? "linear-gradient(180deg, #568d2f 0%, #31581c 100%)"
        : "linear-gradient(180deg, #75402e 0%, #4b271d 100%)",

      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.18), 0 3px 0 rgba(42,16,7,0.7)",

      display: "flex",
      alignItems: "center",
      gap: 7,

      cursor: "pointer",
      boxSizing: "border-box",
    }}
  >
    <img
      src="/pass2.png"
      alt=""
      style={{
        width: 34,
        height: 34,
        flexShrink: 0,
        objectFit: "contain",
      }}
    />

    <div
      style={{
        minWidth: 0,
        flex: 1,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 12,
          lineHeight: "16px",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        OG PASS PLUS
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 2,
          color: battlePassActive
            ? "#c8ff91"
            : "#ff9b8e",
          fontSize: 11,
          lineHeight: "13px",
          fontWeight: 700,
        }}
      >
        {battlePassActive
          ? "ACTIVE"
          : "INACTIVE"}
      </div>
    </div>

    <img
      src="/v2/drop.png"
      alt=""
      draggable={false}
      style={{
        width: 12,
        height: 8,
        flexShrink: 0,
        objectFit: "contain",
        transform: "rotate(-90deg)",
      }}
    />
  </button>
</div>

              {/* LEVEL */}
              <div
                style={{
                  width: "100%",
                  marginTop: 12,
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    minWidth: 0,
                  }}
                >
                  <div
                    className="shadow1"
                    style={{
                      width: 42,
                      height: 42,
                      flex: "0 0 42px",
                      borderRadius: "50%",
                      background: "#8c441f",
                      border: "3px solid #ffe16a",
                      color: "#fff",
                      fontSize: 20,
                      lineHeight: "36px",
                      fontWeight: 900,
                      textAlign: "center",
                      zIndex: 2,
                      boxSizing: "border-box",
                    }}
                  >
                    {level}
                  </div>

                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: 30,
                      marginLeft: -4,
                      background: "#0b0705",
                      border: "3px solid #5d2a13",
                      borderRadius: 5,
                      overflow: "hidden",
                      position: "relative",
                      boxSizing: "border-box",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${levelPercent}%`,
                        background:
                          "linear-gradient(180deg, #b6ffff 0%, #59ccff 55%, #428bff 100%)",
                      }}
                    />

                    <div
                      className="shadow1"
                      style={{
                        position: "absolute",
                        inset: 0,
                        color: "#fff",
                        fontSize: 16,
                        lineHeight: "24px",
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      {level}/30
                    </div>
                  </div>
                </div>
              </div>

              {/* STATS */}
              <div
                style={{
                  width: "100%",
                  marginTop: 10,
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(3, minmax(0, 1fr))",
                  gap: 8,
                  alignItems: "stretch",
                  boxSizing: "border-box",
                }}
              >
                <PublicStatBox
                  icon="/v2/winrate.png"
                  fallbackIcon="/s_attack.png"
                  label="Wins"
                  value={profile?.wins ?? 0}
                />

                <PublicStatBox
                  icon="/v2/trophy.png"
                  fallbackIcon="/v2/trophy.png"
                  label="Trophy"
                  value={
                    profile?.trophy ??
                    profile?.trophies ??
                    0
                  }
                />

                <PublicStatBox
                  icon="/ticket.png"
                  label="Tickets"
                  value={profile?.tickets ?? 0}
                />

                <PublicStatBox
                  icon="/sparks.png"
                  label="Sparks"
                  value={profile?.sparks ?? 0}
                />

                <PublicStatBox
                  icon="/runes.png"
                  label="Runes"
                  value={profile?.runes ?? 0}
                />

                <PublicStatBox
                  icon="/icon_tapi.png"
                  label="$Tapi"
                  value={profile?.tapi ?? 0}
                />
              </div>

              {/* SIDE - READ ONLY */}
              <div
                style={{
                  width: "100%",
                  marginTop: 12,
                  background:
                    "rgba(70, 30, 14, 0.72)",
                  border: "3px solid #5d2a13",
                  borderRadius: 7,
                  padding: "7px 8px",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <img
                  src={sideIcon}
                  alt=""
                  style={{
                    width: 42,
                    height: 42,
                    objectFit: "contain",
                  }}
                />

                <div
                  style={{
                    flex: 1,
                    textAlign: "left",
                  }}
                >
                  <div
                    className="shadow1"
                    style={{
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    CURRENT SIDE
                  </div>

                  <div
                    className="shadow1"
                    style={{
                      color: isMoon
                        ? "#ff6845"
                        : "#ffe16a",
                      fontSize: 18,
                      fontWeight: 700,
                    }}
                  >
                    {sideLabel}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="modal_footer">
          <button
            type="button"
            className="game-button red-button"
            onClick={onClose}
          >
            CLOSE
          </button>
        </div>
      </motion.div>
    </div>
  );
}